/**
 * Mode 2 — Wallpaper (Raster) export, brief 01-export.md.
 *
 * The engine tiles via lattice translations, which are only a literal
 * rectangular "rows × columns" grid for the square/rectangular lattices —
 * hex/oblique/rhombic lattices tile in a skewed or diamond-shaped
 * neighbourhood instead. Rather than force a misleading rows×columns crop on
 * every lattice, the UI offers a single "tiles across" shorthand (2×2/3×3/4×4/
 * custom, matching the brief's own fallback suggestion) that maps to the
 * wallpaper engine's native `rings` parameter — the true, correct placement
 * set for whatever lattice is active. See ExportSheet's Wallpaper panel.
 */
import { downloadCanvasAsPng } from '@/services/download';
import { exportFilename } from '@/services/exportFilename';
import { renderWallpaperTiles } from '@/services/tileRenderer';
import { TILE_RASTER } from '@/stores/layers';

export interface WallpaperExportOptions {
  /** "how many tiles across" shorthand — 2/3/4/custom */
  tilesAcross: number;
  /** Fill the theme paper colour behind the tiling. Default true; false emits a
   *  transparent PNG (the raw tile composite renderWallpaperTiles produces). */
  background?: boolean;
}

export function tilesAcrossToRings(tilesAcross: number): number {
  return Math.max(1, Math.ceil(tilesAcross / 2));
}

/** Composite the tile canvas over the editor's paper colour (`--canvas-bg`). */
function withBackground(tiles: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = tiles.width;
  canvas.height = tiles.height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--canvas-bg').trim() || '#14121b';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(tiles, 0, 0);
  return canvas;
}

export function exportWallpaper(opts: WallpaperExportOptions): void {
  const tiles = renderWallpaperTiles(tilesAcrossToRings(opts.tilesAcross), TILE_RASTER);
  const canvas = opts.background === false ? tiles : withBackground(tiles);
  downloadCanvasAsPng(canvas, exportFilename('png'));
}
